import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message } from "antd";
import { apis, AUTH_HEADER_NAME } from "common/apis";
import { routes } from "common/routes";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";
import { removeProcessingSocietySequenceDay, setProcessingSocietySequenceDay } from "store/admin-slice/adminServicesSlice";
import { IPickerMappingUpload, IPickerMappingUploadRow } from "common/interface/IPickerUploadScheme";

const UploadSocietySequencePage = () => {
  useDocumentTitle("Upload Society Sequence Order");
  const { authToken } = useAppSelector((state) => state.app.user);
  const { processingSocietySequenceMapping } = useAppSelector((state) => state.adminServices);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);

  useEffect(() => {
    const { timeUploaded } = processingSocietySequenceMapping;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingSocietySequenceDay());
      }
    }

    if (processingSocietySequenceMapping && processingSocietySequenceMapping.key) {
      navigate(`/${routes.ADMIN_SERVICES.SAVE_SOCIETY_SEQUENCE}`, {});
    }

    return () => {
      message.destroy();
    };
  }, [processingSocietySequenceMapping, navigate, dispatch]);

  useEffect(() => {
    const failed = processingSocietySequenceMapping?.failedRows?.length;

    if (failed) {
      setHasErrorRows(true);
    }
  }, [processingSocietySequenceMapping?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: IPickerMappingUpload) => {
    dispatch(setProcessingSocietySequenceDay(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingSocietySequenceDay());
  };

  // useEffect(() => {
  //   return (() => {
  //     dispatch(removeProcessingPO())
  //   });
  // }, []);

  const { headerMapping } = processingSocietySequenceMapping;
  const headerMappingWithError = headerMapping + ",errors:Errors";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingSocietySequenceMapping?.successRows || [];
  const failedRows = processingSocietySequenceMapping?.failedRows || [];
  const successData = successRows?.map((row, i) => ({
    ...row,
    key: `data-success-${i}`,
  }));
  const failedData = failedRows?.map((row, i) => ({
    ...row,
    key: `data-failed-${i}`,
  }));
  const data = [...failedData, ...successData];
  const columns: ColumnsType<IPickerMappingUploadRow & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: IPickerMappingUploadRow & { key: string }) => {
        if (key === "errors") {
          const errorMessages = record.errors
            ?.map((error) => error.message)
            .join(", ");
          return <>{errorMessages}</>;
        }
        return <>{text}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (record: IPickerMappingUploadRow & { key: string }) => {
    if (record.errors && record.errors.length) {
      return "error-row";
    }

    return "";
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Society Sequence" />
        </Col>
      </Row>

      <Row>
        <Col lg={16} xs={24}>
          <AppUpload
            uploadUrl={apis.uploadSocietySequenceMapping}
            headers={headers}
            onUploaded={onUploaded}
            onUploading={onUploading}
            hasErrorRows={hasErrorRows}
          />
        </Col>
      </Row>

      {!!Object.keys(processingSocietySequenceMapping).length && !processingSocietySequenceMapping.key && (
        <>
          <Row>
            <Col lg={16} xs={24}>
              <Alert
                type="error"
                message="Errors in sheet data."
                description="Please correct errors and upload again."
                showIcon
                banner
              />
            </Col>
          </Row>

          <Row>
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              rowClassName={(record) => addBorderToErrorRows(record)}
              scroll={{ y: 500, x: "100vh" }}
              className="table-with-errors"
            ></Table>
          </Row>
        </>
      )}
    </>
  );
};

export default UploadSocietySequencePage;
